import { FC, HTMLProps, useState } from 'react';
import { cx } from '@emotion/css';
import Image from 'next/legacy/image';
import { useTranslation } from 'next-i18next';

import { Spinner } from '@phoenix/design-system/frontend/ui/spinner/spinner';

import { PlaceholderImage } from '../placeholder-image';
import { GalleryImage } from './images-gallery';

interface ImageThumbnailProps extends HTMLProps<HTMLDivElement> {
  image: GalleryImage;
  selectedImageId?: string;
  customImageRender?: (image: GalleryImage) => void;
  onClick?: () => void;
}

export const ImageThumbnail: FC<React.PropsWithChildren<ImageThumbnailProps>> = ({
  image,
  selectedImageId,
  customImageRender,
  onClick,
}) => {
  const { t } = useTranslation('shared');
  const [isImageUnsupported, setIsImageUnsupported] = useState(false);
  const isImageUnavailable = !image.url || image.status === 'error';

  return (
    <button
      className={cx(
        'flex min-h-[4.375rem] w-[4.375rem] flex-col items-center justify-center focus-visible:outline outline-primary-black outline-2 outline-offset-2',
        image.status === 'uploading' ? 'cursor-wait' : 'cursor-pointer'
      )}
      onClick={onClick}
      aria-label={`${t('shared_imageUpload_thumbnail_aria')} (${image.originalFilename})`}
    >
      {isImageUnavailable || isImageUnsupported ? (
        <div className="flex h-full aspect-square items-center justify-center self-center bg-secondary-light opacity-50">
          <PlaceholderImage className="h-4 text-secondary" />
        </div>
      ) : (
        <div className="relative h-full aspect-square" data-testid={`gallery-thumbnail_${image.id}`}>
          {customImageRender ? (
            <>{customImageRender(image)}</>
          ) : (
            <Image
              className="h-[4.375rem] w-[4.375rem]"
              src={image?.thumbnail ? image.thumbnail : image.url}
              alt={
                image.id === 'system-image-id'
                  ? `${t('shared_imageUpload_system_alt')} (${image.originalFilename})`
                  : `${t('shared_imageUpload_custom_alt')} (${image.originalFilename})`
              }
              layout="fill"
              onError={() => setIsImageUnsupported(true)}
              objectFit="contain"
              unoptimized={true}
            />
          )}
          {image.status === 'uploading' && (
            <div className="absolute right-[-0.625rem] top-[-0.625rem] h-5 w-5 rounded-half border-[0.13rem] border-primary-white">
              <Spinner className="h-4 w-4 rounded-half border-[0.15rem] border-secondary-mid border-l-primary-white bg-primary-white text-[0.625rem] after:w-4" />
            </div>
          )}
        </div>
      )}
      <div className={cx({ invisible: image.id !== selectedImageId }, 'mt-2 h-1 w-full bg-primary-black')} />
    </button>
  );
};
